import { useQuery } from '@tanstack/react-query';

import { listBanStatusChannels } from 'api/adminBanStatus';
import { queryKeys } from 'constants/queryKeys';
import { banStatusStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import type {
  TBanStatusChannel,
  TListBanStatusChannelsRequest,
} from 'types';
import { toErrorMessage } from './utils';

type UseAdminBanStatusChannelsResult = {
  channels: TBanStatusChannel[];
  total: number;
  currentPage: number;
  totalPage: number;
  loading: boolean;
  refreshing: boolean;
  error: string | undefined;
  refresh: () => void;
};

/**
 * 払い出しできないチャンネルの一覧。
 *
 * 絞り込みとページングはサーバ側で行う。クライアント側で切ると、
 * 打ち切られた分に検索が届かず、件数表示が集計と食い違う。
 */
export function useAdminBanStatusChannels(
  params: TListBanStatusChannelsRequest = {},
): UseAdminBanStatusChannelsResult {
  const { user } = useAuth();
  const currentPage = params.currentPage ?? 1;
  const pageSize = params.pageSize ?? 20;
  const filters = {
    organizationId: params.organizationId,
    category: params.category,
    keyword: params.keyword?.trim() || undefined,
  };

  const query = useQuery({
    queryKey: [
      queryKeys.LINE_CHANNEL_BAN_STATUS_CHANNELS,
      user?.uid,
      currentPage,
      pageSize,
      filters,
    ],
    enabled: Boolean(user?.uid),
    queryFn: () => listBanStatusChannels({ currentPage, pageSize, ...filters }),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  return {
    channels: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    currentPage: query.data?.currentPage ?? currentPage,
    totalPage: query.data?.totalPage ?? 1,
    // 絞り込みを変えるたびに新しいキーになるので、直前の結果は保持しない。
    // 保持すると別組織の行を新しい組織名の下に出してしまう。
    loading: Boolean(user?.uid) && query.isPending,
    refreshing: query.isFetching && !query.isPending,
    error: query.error
      ? toErrorMessage(query.error, banStatusStrings.errors.loadChannelsFailed)
      : undefined,
    refresh: () => {
      void query.refetch();
    },
  };
}
